/**
 * Timeline Editor Store — Zustand state for the composition editor.
 *
 * Data model (tracks):
 *   videoClips  — real videos on BASE layer (v0) — sequential, cover full canvas
 *   imageClips  — images on BASE layer (v0)      — sequential, cover full canvas
 *   overlayV1Clips — anything on overlay layer 1 (positioned on top of base)
 *   overlayV2Clips — anything on overlay layer 2 (positioned on top of V1)
 *   audioClips  — audio tracks
 *
 * Overlay clips carry a `transform` object: { x_pct, y_pct, scale, rotation,
 * opacity, corner_radius } that defines where on the preview canvas they sit.
 * All values are normalized 0..1 (position & scale) so the same composition
 * looks identical regardless of output resolution.
 */
import { create } from 'zustand';
import {
  CLIP_AUDIO_DEFAULTS, MIX_DEFAULTS, TRACK_DEFAULTS, TRACK_IDS,
  effectiveGain, normalizeTrack,
} from '../utils/audioModel';
import {
  ELEMENT_MODEL_VERSION, applyPreset, normalizeElement, normalizeElements,
  normalizeTextStyle, normalizeTransform,
} from '../utils/overlayModel';
import {
  TIMELINE_SERVER_SYNC, fetchTimeline, isConflict, putTimeline, serverVersionFrom,
} from '../services/timelineSync';
import {
  closeGap as closeGapIn, duplicateAfter, isSequential, removeWithRipple, replaceContent,
  shiftFrom,
} from '../utils/timelineEdit';

// Envío AP-3 · preferencias de edición (por navegador).
export const PREF_MAGNET = 'sml.tl.magnet';
export const PREF_CLIP_PILL = 'sml.tl.clipPill';
const readBoolPref = (key, fallback) => {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    return v === null ? fallback : v === '1';
  } catch (_e) { return fallback; }
};
const writeBoolPref = (key, value) => {
  try { localStorage.setItem(key, value ? '1' : '0'); } catch (_e) { /* modo privado */ }
};

/**
 * Orden de capas, de ARRIBA (se dibuja encima) a ABAJO. Única fuente de
 * verdad del apilado: ordena los lanes del editor, el z-index del preview y
 * las capas del render (`timeline_service.render_layers_from_order`).
 * Los documentos viejos (sin `trackOrder`) usan este orden, que reproduce
 * exactamente cómo exportaba el worker antes del cambio.
 */
export const DEFAULT_TRACK_ORDER = ['text', 'sticker', 'v2', 'v1', 'image', 'video', 'audio'];

export const normalizeTrackOrder = (raw) => {
  const order = [];
  (Array.isArray(raw) ? raw : []).forEach((t) => {
    if (DEFAULT_TRACK_ORDER.includes(t) && !order.includes(t)) order.push(t);
  });
  DEFAULT_TRACK_ORDER.forEach((t) => { if (!order.includes(t)) order.push(t); });
  return order;
};

/** Versión del proyecto guardado (timeline completo). */
export const TIMELINE_SCHEMA_VERSION = 2;
/** Plantillas de estilo de texto: globales al usuario, no al proyecto. */
export const TEXT_TEMPLATES_KEY = 'sml.textTemplates.v1';
export const TEXT_TEMPLATE_SCHEMA_VERSION = 1;

/** trackKind del editor → trackId del modelo de audio. */
export const trackIdFor = (trackKind) =>
  trackKind === 'audio' ? 'audio'
  : trackKind === 'v1' ? 'v1'
  : trackKind === 'v2' ? 'v2'
  : 'base';

// ── E6 · Timeline en vivo (LMX) ─────────────────────────────────────────
/** Firma del documento: los mismos campos que mira el autosave del editor.
 *  Acepta el estado del store o un doc serializado (text_variables/template_ref). */
export const docSignatureOf = (d) => JSON.stringify([
  d.videoClips, d.imageClips, d.overlayV1Clips, d.overlayV2Clips, d.audioClips,
  d.elements, d.videoTransitions, d.tracks, d.masterMuted, d.masterVolume,
  d.resolution, d.fps, d.slots,
  d.textVariables ?? d.text_variables,
  d.templateRef ?? d.template_ref,
  d.trackOrder,
]);

const CLIP_LIST_KEYS = ['videoClips', 'imageClips', 'overlayV1Clips', 'overlayV2Clips', 'audioClips'];

/** id → JSON de cada clip/elemento/transición (para el diff del sync en vivo). */
const snapshotByIds = (s) => {
  const map = {};
  CLIP_LIST_KEYS.forEach((k) => (s[k] || []).forEach((c) => { map[c.id] = JSON.stringify(c); }));
  (s.elements || []).forEach((el) => { map[el.element_id] = JSON.stringify(el); });
  (s.videoTransitions || []).forEach((t) => { map[t.id] = JSON.stringify(t); });
  return map;
};

/** Ids nuevos o cambiados (los borrados desaparecen solos: no se pueden destellar). */
const diffSnapshotIds = (before, after) =>
  Object.keys(after).filter((id) => before[id] !== after[id]);

/** ¿La selección sigue existiendo tras una hidratación en vivo? */
const selectionExists = (s, sel) => {
  if (!sel) return true;
  if (sel.trackKind === 'transition') return s.videoTransitions.some((t) => t.id === sel.id);
  if (sel.trackKind === 'text' || sel.trackKind === 'sticker') {
    return s.elements.some((el) => el.element_id === sel.id);
  }
  const key = keyFor(sel.trackKind);
  return !!key && (s[key] || []).some((c) => c.id === sel.id);
};

let saveInFlight = null;   // AP-9 · guardado en curso
let saveAgain = false;     // hubo cambios mientras se guardaba
let flashTimer = null;

const genId = () => `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** Escala base del timeline: px por segundo con zoom = 1.
 *  80 px/s deja ~24 s por pantalla: el lienzo es varias veces más ancho que el
 *  viewport, así el pulgar de la barra horizontal queda chico y el arrastre
 *  recorre de verdad todo el timeline. Con el zoom (0.25×–4×) se abre o cierra. */
export const BASE_PX_PER_SEC = 80;
export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 4;
/** Segundos de aire después del último elemento para poder soltar contenido. */
export const TIMELINE_TAIL_SECONDS = 4;

const DEFAULT_TRANSFORM = {
  x_pct: 0.5,          // center X (0 = left edge, 1 = right edge)
  y_pct: 0.5,          // center Y
  scale: 0.4,          // clip occupies 40% of canvas width
  rotation: 0,         // degrees
  opacity: 1.0,        // 0..1
  corner_radius: 0,    // 0..1 of min(w,h)/2 — CSS-only for now
};

const isOverlayKey = (k) => k === 'v1' || k === 'v2';
const keyFor = (trackKind) =>
  trackKind === 'video' ? 'videoClips'
  : trackKind === 'image' ? 'imageClips'
  : trackKind === 'audio' ? 'audioClips'
  : trackKind === 'v1' ? 'overlayV1Clips'
  : trackKind === 'v2' ? 'overlayV2Clips'
  : null;

/**
 * E2 — compatibilidad pista↔clip: exactamente lo que el renderer sabe
 * dibujar (misma matriz que `timeline_service.TRACKS` en el backend).
 * `video` = pista base de video, `image` = pista de imágenes, los overlays
 * aceptan ambos, y el audio sólo audio.
 */
export const trackAcceptsKind = (trackKind, clipKind) => {
  const kind = clipKind || 'video';
  if (trackKind === 'video') return kind === 'video';
  if (trackKind === 'image') return kind === 'image';
  if (trackKind === 'v1' || trackKind === 'v2') return kind === 'video' || kind === 'image';
  if (trackKind === 'audio') return kind === 'audio';
  return false;
};

const useTimelineStore = create((set, get) => ({
  // ── UI state ─────────────────────────────────────────────────
  isOpen: false,
  selectedClip: null,     // { trackKind, id }
  resolution: '1080p',
  fps: 30,
  playheadTime: 0,
  isPlaying: false,
  zoom: 1,

  // ── Tracks ───────────────────────────────────────────────────
  videoClips: [],
  imageClips: [],
  overlayV1Clips: [],
  overlayV2Clips: [],
  audioClips: [],

  /**
   * Elementos estáticos (texto y stickers) — modelo canónico v1, ver
   * `utils/overlayModel.js`. Un solo array ordenado por `z_order`: el preview,
   * el proyecto guardado, el manifiesto y FFmpeg leen exactamente esto.
   */
  elements: [],

  /**
   * Video-to-video transitions on the BASE video track.
   * Each entry: {
   *   id, before_id, after_id,          // IDs of the two adjacent video clips
   *   type: 'fade_to_black',            // only supported type for now
   *   duration: 1.0,                    // total transition length (s)
   *   out_duration: 0.5,                // fade-out of `before_id` (s)
   *   in_duration:  0.5,                // fade-in  of `after_id`  (s)
   *   symmetric: true                   // when true, out=in=duration/2
   * }
   * Materialized into per-clip fade_in/fade_out at export time — the
   * backend needs no new logic.
   */
  videoTransitions: [],

  /**
   * E3 · plantillas: huecos reemplazables y variables de texto del documento.
   * Viven en el timeline guardado y se serializan tal cual, así el servidor
   * sigue siendo la única fuente de verdad.
   */
  slots: [],
  textVariables: [],
  templateRef: null,

  /** Orden de capas del documento (arriba = encima). */
  trackOrder: [...DEFAULT_TRACK_ORDER],

  setTrackOrder: (order) => set({ trackOrder: normalizeTrackOrder(order) }),

  /** Sube (-1) o baja (+1) una pista. Sus clips se mueven con ella. */
  moveTrack: (trackId, delta) => {
    const order = [...get().trackOrder];
    const from = order.indexOf(trackId);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= order.length) return false;
    order.splice(to, 0, order.splice(from, 1)[0]);
    set({ trackOrder: order });
    return true;
  },

  /** z-index del apilado: la pista de abajo empieza en 1. */
  layerZ: (trackId) => {
    const order = get().trackOrder;
    const idx = order.indexOf(trackId);
    return idx < 0 ? 1 : (order.length - idx);
  },

  // ── Audio (modelo canónico v1, ver utils/audioModel.js) ──────
  tracks: {
    base:  { ...TRACK_DEFAULTS },
    v1:    { ...TRACK_DEFAULTS },
    v2:    { ...TRACK_DEFAULTS },
    audio: { ...TRACK_DEFAULTS },
  },
  masterMuted: MIX_DEFAULTS.master_muted,
  masterVolume: MIX_DEFAULTS.master_volume,

  setTrackAudio: (trackId, patch) => set((s) => (
    TRACK_IDS.includes(trackId)
      ? { tracks: { ...s.tracks, [trackId]: normalizeTrack({ ...s.tracks[trackId], ...patch }) } }
      : {}
  )),
  toggleTrackMute: (trackId) => set((s) => (
    TRACK_IDS.includes(trackId)
      ? { tracks: { ...s.tracks, [trackId]: { ...s.tracks[trackId], muted: !s.tracks[trackId].muted } } }
      : {}
  )),
  toggleTrackSolo: (trackId) => set((s) => (
    TRACK_IDS.includes(trackId)
      ? { tracks: { ...s.tracks, [trackId]: { ...s.tracks[trackId], solo: !s.tracks[trackId].solo } } }
      : {}
  )),
  setMasterMuted: (value) => set({ masterMuted: !!value }),
  setMasterVolume: (value) => set({ masterVolume: Math.max(0, Math.min(2, Number(value) || 0)) }),

  /** Ganancia efectiva de un clip: idéntica a la que aplica la exportación. */
  clipGain: (trackKind, clip) => {
    const s = get();
    const trackId = trackIdFor(trackKind);
    return effectiveGain(clip, trackId, s.tracks,
      { master_muted: s.masterMuted, master_volume: s.masterVolume },
      { isVisual: trackId !== 'audio' });
  },


  renderJob: null,

  // ── UI actions ───────────────────────────────────────────────
  openEditor:   () => set({ isOpen: true }),
  closeEditor:  () => set({ isOpen: false, isPlaying: false }),
  toggleEditor: () => set((s) => ({ isOpen: !s.isOpen, isPlaying: false })),

  setResolution: (r) => set({ resolution: r }),
  setPlayhead:   (t) => set({ playheadTime: Math.max(0, t) }),
  selectClip:    (sel) => set({ selectedClip: sel }),
  setPlaying:    (p) => set({ isPlaying: !!p }),

  // ── Zoom del timeline (regla, pistas, clips y playhead comparten escala) ──
  setZoom: (z) => set({ zoom: Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Number(z) || 1)) }),
  zoomIn:  () => set((s) => ({ zoom: Math.min(ZOOM_MAX, +(s.zoom * 1.25).toFixed(3)) })),
  zoomOut: () => set((s) => ({ zoom: Math.max(ZOOM_MIN, +(s.zoom / 1.25).toFixed(3)) })),
  resetZoom: () => set({ zoom: 1 }),

  _keyFor: keyFor,

  // ── Clip CRUD ────────────────────────────────────────────────
  addVideoClip: (meta) => {
    const kind = meta.kind || 'video';
    if (kind === 'audio') return get().addAudioClip(meta);

    // Explicit track: 'base' | 'v1' | 'v2'. Default to 'base'.
    const targetTrack = meta.track || 'base';
    const trackKind = targetTrack === 'v1' ? 'v1'
                    : targetTrack === 'v2' ? 'v2'
                    : kind === 'image' ? 'image' : 'video';
    const key = keyFor(trackKind);
    const list = get()[key];
    const isOverlay = isOverlayKey(trackKind);

    // Overlays stack independently — don't snap to previous clip's end; start at current playhead.
    const trackEnd = list.reduce((acc, c) => Math.max(acc, c.start_time + c.duration), 0);
    const defaultDur = meta.duration ?? (kind === 'image' ? 3.0 : Math.min(meta.original_duration || 5, 10));

    // Posición pedida (cabezal) si no se superpone con lo que ya hay en la
    // pista; si se superpone, va al final para no romper la composición.
    // La pista de AUDIO sí permite superponer (voz + música suenan juntas:
    // el render las mezcla con amix), así que respeta el cabezal.
    const allowsOverlap = isOverlay || trackKind === 'audio';
    let startTime = allowsOverlap ? Math.max(0, get().playheadTime || 0) : trackEnd;
    if (meta.start_time != null) {
      const wanted = Math.max(0, meta.start_time);
      const overlaps = !allowsOverlap && list.some(
        (c) => wanted < c.start_time + c.duration && wanted + defaultDur > c.start_time
      );
      startTime = overlaps ? trackEnd : wanted;
    }

    const clip = {
      id: genId(),
      source_url: meta.source_url,
      kind,
      start_time: startTime,
      duration: defaultDur,
      trim_start: 0,
      original_duration: meta.original_duration || null,
      fade_in: 0,
      fade_out: 0,
      thumb_url: meta.thumb_url || (kind === 'image' ? meta.source_url : null),
      label: meta.label || (kind === 'image' ? 'Imagen' : 'Video'),
      // E2 — trazabilidad: de dónde vino este clip.
      asset_id: meta.asset_id || null,
      source: meta.source || null,
      // Audio embebido: por defecto el clip conserva su sonido original.
      ...CLIP_AUDIO_DEFAULTS,
      ...(isOverlay ? { transform: { ...DEFAULT_TRANSFORM } } : {}),
    };
    set({
      [key]: [...list, clip],
      selectedClip: { trackKind, id: clip.id },
    });
    return clip.id;
  },

  addAudioClip: (meta) => {
    const list = get().audioClips;
    const lastEnd = list.reduce((acc, c) => Math.max(acc, c.start_time + c.duration), 0);
    // La pista de audio admite superposición (voz sobre música): si piden un
    // start explícito se respeta, en vez de empujar el clip al final.
    const startTime = meta.start_time != null
      ? Math.max(0, meta.start_time)
      : lastEnd;
    const clip = {
      id: genId(),
      source_url: meta.source_url,
      start_time: startTime,
      duration: meta.original_duration || 10,
      trim_start: 0,
      original_duration: meta.original_duration || null,
      loop: false,
      volume: 1.0,
      fade_in: 0,
      fade_out: 0,
      label: meta.label || 'Audio',
      muted: CLIP_AUDIO_DEFAULTS.muted,
      // E2 — trazabilidad.
      asset_id: meta.asset_id || null,
      source: meta.source || null,
    };
    set({
      audioClips: [...list, clip],
      selectedClip: { trackKind: 'audio', id: clip.id },
    });
    return clip.id;
  },

  updateClip: (trackKind, id, patch) => {
    const key = keyFor(trackKind);
    if (!key) return;
    set((s) => ({
      [key]: s[key].map((c) => (c.id === id ? { ...c, ...patch } : c)),
    }));
  },

  updateTransform: (trackKind, id, patch) => {
    if (!isOverlayKey(trackKind)) return;
    const key = keyFor(trackKind);
    set((s) => ({
      [key]: s[key].map((c) =>
        c.id === id
          ? { ...c, transform: { ...DEFAULT_TRANSFORM, ...(c.transform || {}), ...patch } }
          : c
      ),
    }));
  },

  removeClip: (trackKind, id) => {
    const key = keyFor(trackKind);
    if (!key) return null;
    const s = get();
    // AP-3 · con el imán, en las pistas base lo que sigue se corre y tapa el hueco.
    const ripple = s.magnet && isSequential(trackKind);
    const out = removeWithRipple(s[key], id, { ripple });
    if (!out.removed) return null;
    set({
      [key]: out.clips,
      selectedClip: s.selectedClip?.id === id ? null : s.selectedClip,
      // Drop any transitions that referenced this clip.
      videoTransitions: s.videoTransitions.filter(
        (t) => t.before_id !== id && t.after_id !== id
      ),
      lastUndo: {
        label: out.removed.label || 'Clip',
        patch: { [key]: s[key], videoTransitions: s.videoTransitions },
      },
    });
    return { label: out.removed.label || 'Clip', closed: out.closed };
  },

  // ── Envío AP-3 · edición rápida (barra del timeline y menú sobre el clip) ──
  magnet: readBoolPref(PREF_MAGNET, true),
  clipPill: readBoolPref(PREF_CLIP_PILL, true),
  snapGuide: null,          // segundo donde se dibuja la guía del imán al arrastrar
  lastUndo: null,           // un nivel de deshacer para borrar / cerrar hueco / reemplazar

  setMagnet: (value) => { writeBoolPref(PREF_MAGNET, !!value); set({ magnet: !!value }); },
  setClipPill: (value) => { writeBoolPref(PREF_CLIP_PILL, !!value); set({ clipPill: !!value }); },
  setSnapGuide: (t) => set({ snapGuide: Number.isFinite(t) ? t : null }),

  undoLast: () => {
    const u = get().lastUndo;
    if (!u) return false;
    set({ ...u.patch, lastUndo: null });
    return true;
  },

  closeGapAt: (trackKind, gapEnd, size) => {
    const key = keyFor(trackKind);
    if (!key || !(size > 0)) return;
    const s = get();
    set({
      [key]: closeGapIn(s[key], gapEnd, size),
      lastUndo: { label: 'Espacio', patch: { [key]: s[key] } },
    });
  },

  duplicateClip: (trackKind, id) => {
    const key = keyFor(trackKind);
    if (!key) return null;
    const s = get();
    const clip = s[key].find((c) => c.id === id);
    if (!clip) return null;
    const copy = duplicateAfter(clip, genId());
    let list = s[key];
    if (isSequential(trackKind)) {
      // Se abre lugar: lo que estaba después se corre el largo de la copia.
      list = shiftFrom(list, copy.start_time, clip.duration, clip.id);
    }
    set({ [key]: [...list, copy], selectedClip: { trackKind, id: copy.id } });
    return copy.id;
  },

  /**
   * Cambia el contenido de un clip sin tocar su posición, fundidos, volumen ni
   * transform. `keepDuration` false: toma el largo del nuevo y, en las pistas
   * base, corre lo que sigue. Devuelve false si la pista no acepta ese tipo.
   */
  replaceClip: (trackKind, id, meta, { keepDuration = true } = {}) => {
    const key = keyFor(trackKind);
    if (!key) return false;
    const s = get();
    const clip = s[key].find((c) => c.id === id);
    if (!clip) return false;
    const kind = meta.kind || (trackKind === 'audio' ? 'audio' : 'video');
    if (trackKind !== 'audio' && !trackAcceptsKind(trackKind, kind)) return false;
    if (trackKind === 'audio' && kind !== 'audio') return false;
    const { clip: next, delta } = replaceContent(clip, { ...meta, kind }, { keepDuration });
    if (trackKind === 'audio') delete next.kind;   // los clips de audio no llevan `kind`
    delete next.edit;                              // AP-4 · otro archivo: ya no es «editada»
    let list = s[key].map((c) => (c.id === id ? next : c));
    const shift = isSequential(trackKind) && delta !== 0 && (delta > 0 || s.magnet);
    if (shift) list = shiftFrom(list, clip.start_time + clip.duration, delta, id);
    set({
      [key]: list,
      selectedClip: { trackKind, id },
      lastUndo: { label: clip.label || 'Clip', patch: { [key]: s[key] } },
    });
    return true;
  },

  // ── Envío AP-4 · «Editar imagen» ──────────────────────────────
  imageEdit: null,          // { trackKind, id } del clip que se está editando sobre el preview

  /** Abre la edición sobre el preview: pausa y lleva el cabezal al clip si hace falta. */
  startImageEdit: (trackKind, id) => {
    const key = keyFor(trackKind);
    const s = get();
    const clip = key ? s[key].find((c) => c.id === id) : null;
    if (!clip || (clip.kind || (trackKind === 'image' ? 'image' : 'video')) !== 'image') return false;
    const t = s.playheadTime;
    const inside = t >= clip.start_time && t < clip.start_time + clip.duration;
    set({
      imageEdit: { trackKind, id },
      selectedClip: { trackKind, id },
      isPlaying: false,
      ...(inside ? {} : { playheadTime: clip.start_time + Math.min(0.1, clip.duration / 2) }),
    });
    return true;
  },
  stopImageEdit: () => set({ imageEdit: null }),

  /**
   * El clip pasa a usar la imagen NUEVA. Se guarda de dónde salió (`edit.original`,
   * siempre la primera) y los ajustes, para volver a editar sin degradar.
   */
  applyImageEdit: (trackKind, id, { url, assetId, name, params }) => {
    const key = keyFor(trackKind);
    if (!key || !url) return false;
    const s = get();
    const clip = s[key].find((c) => c.id === id);
    if (!clip) return false;
    const original = clip.edit?.original || {
      source_url: clip.source_url, thumb_url: clip.thumb_url || null,
      asset_id: clip.asset_id || null, label: clip.label || null,
    };
    const next = {
      ...clip,
      source_url: url,
      thumb_url: url,
      asset_id: assetId || null,
      label: String(name || clip.label || 'Imagen').replace(/\.png$/i, ''),
      edit: { original, params },
    };
    set({
      [key]: s[key].map((c) => (c.id === id ? next : c)),
      lastUndo: { label: clip.label || 'Imagen', patch: { [key]: s[key] } },
    });
    return true;
  },

  /** Vuelve a la imagen de origen (la editada queda en la Galería). */
  restoreImageOriginal: (trackKind, id) => {
    const key = keyFor(trackKind);
    if (!key) return false;
    const s = get();
    const clip = s[key].find((c) => c.id === id);
    const o = clip?.edit?.original;
    if (!o?.source_url) return false;
    const { edit, ...rest } = clip;
    const next = {
      ...rest, source_url: o.source_url, thumb_url: o.thumb_url || o.source_url,
      asset_id: o.asset_id || null, label: o.label || rest.label,
    };
    set({
      [key]: s[key].map((c) => (c.id === id ? next : c)),
      lastUndo: { label: clip.label || 'Imagen', patch: { [key]: s[key] } },
    });
    return true;
  },

  // ── Transition CRUD (base video track only) ───────────────────
  addTransition: ({ before_id, after_id, type = 'fade_to_black', duration = 1.0 } = {}) => {
    if (!before_id || !after_id) return null;
    const state = get();
    // Reject duplicates for the same gap.
    if (state.videoTransitions.some((t) => t.before_id === before_id && t.after_id === after_id)) {
      return null;
    }
    const half = duration / 2;
    const t = {
      id: genId(),
      before_id,
      after_id,
      type,
      duration,
      out_duration: half,
      in_duration: half,
      symmetric: true,
    };
    set((s) => ({
      videoTransitions: [...s.videoTransitions, t],
      selectedClip: { trackKind: 'transition', id: t.id },
    }));
    return t.id;
  },

  updateTransition: (id, patch) => {
    set((s) => ({
      videoTransitions: s.videoTransitions.map((t) => {
        if (t.id !== id) return t;
        const next = { ...t, ...patch };
        // Guard: keep durations non-negative and consistent when symmetric.
        if (next.symmetric) {
          const total = Math.max(0, Number(next.duration) || 0);
          next.duration = total;
          next.out_duration = total / 2;
          next.in_duration = total / 2;
        } else {
          next.out_duration = Math.max(0, Number(next.out_duration) || 0);
          next.in_duration = Math.max(0, Number(next.in_duration) || 0);
          next.duration = next.out_duration + next.in_duration;
        }
        return next;
      }),
    }));
  },

  removeTransition: (id) => {
    set((s) => ({
      videoTransitions: s.videoTransitions.filter((t) => t.id !== id),
      selectedClip: s.selectedClip?.id === id && s.selectedClip?.trackKind === 'transition'
        ? null : s.selectedClip,
    }));
  },

  splitClip: (trackKind, id, time) => {
    const key = keyFor(trackKind);
    if (!key) return;
    const clip = get()[key].find((c) => c.id === id);
    if (!clip) return;
    const relTime = time - clip.start_time;
    if (relTime <= 0.05 || relTime >= clip.duration - 0.05) return;
    const first = { ...clip, duration: relTime, fade_out: 0 };
    const second = {
      ...clip,
      id: genId(),
      start_time: clip.start_time + relTime,
      duration: clip.duration - relTime,
      trim_start: (clip.trim_start || 0) + relTime,
      fade_in: 0,
    };
    set((s) => ({
      [key]: s[key].flatMap((c) => (c.id === id ? [first, second] : [c])),
    }));
  },

  clearTimeline: () =>
    set({
      videoClips: [], imageClips: [],
      overlayV1Clips: [], overlayV2Clips: [],
      audioClips: [],
      elements: [],
      videoTransitions: [],
      slots: [],
      textVariables: [],
      templateRef: null,
      trackOrder: [...DEFAULT_TRACK_ORDER],
      tracks: {
        base:  { ...TRACK_DEFAULTS },
        v1:    { ...TRACK_DEFAULTS },
        v2:    { ...TRACK_DEFAULTS },
        audio: { ...TRACK_DEFAULTS },
      },
      masterMuted: MIX_DEFAULTS.master_muted,
      masterVolume: MIX_DEFAULTS.master_volume,
      selectedClip: null, playheadTime: 0, isPlaying: false,
    }),

  // ── Computed ─────────────────────────────────────────────────
  getTotalDuration: () => {
    const s = get();
    const end = (list) => list.reduce((acc, c) => Math.max(acc, c.start_time + c.duration), 0);
    const elementsEnd = s.elements.reduce(
      (acc, el) => Math.max(acc, (el.start || 0) + (el.duration || 0)), 0);
    return Math.max(
      end(s.videoClips), end(s.imageClips),
      end(s.overlayV1Clips), end(s.overlayV2Clips),
      end(s.audioClips), elementsEnd, 0
    );
  },

  // Overlay clips active at a given time (for live preview).
  // Returns: [{ clip, trackKind }] sorted by stacking order (v1 below v2).
  getActiveOverlays: (t) => {
    const s = get();
    const pick = (list, trackKind) =>
      list.filter((c) => t >= c.start_time && t < c.start_time + c.duration)
          .map((c) => ({ clip: c, trackKind }));
    return [...pick(s.overlayV1Clips, 'v1'), ...pick(s.overlayV2Clips, 'v2')];
  },

  // ── Elementos estáticos: texto y stickers ────────────────────
  addTextElement: ({ preset = 'title', start, duration = 3, content } = {}) => {
    const state = get();
    const { style, transform } = applyPreset(preset);
    const element = normalizeElement({
      element_id: `el_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      type: 'text',
      track_id: 'text',
      start: start != null ? start : Math.max(0, state.playheadTime || 0),
      duration,
      transform,
      style: content ? { ...style, content } : style,
      z_order: state.elements.length,
      label: 'Texto',
    });
    set({
      elements: [...state.elements, element],
      selectedClip: { trackKind: 'text', id: element.element_id },
    });
    return element.element_id;
  },

  addStickerElement: ({ source_url, asset_id, label, start, duration = 3,
                        scale } = {}) => {
    if (!source_url) return null;
    const state = get();
    const element = normalizeElement({
      element_id: `el_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      type: 'sticker',
      track_id: 'sticker',
      asset_id: asset_id || null,
      source_url,
      start: start != null ? start : Math.max(0, state.playheadTime || 0),
      duration,
      transform: normalizeTransform({ scale: scale ?? 0.25 }),
      z_order: state.elements.length,
      label: label || 'Sticker',
    });
    set({
      elements: [...state.elements, element],
      selectedClip: { trackKind: 'sticker', id: element.element_id },
    });
    return element.element_id;
  },

  updateElement: (elementId, patch) => set((s) => ({
    elements: s.elements.map((el) => (
      el.element_id === elementId ? normalizeElement({ ...el, ...patch }) : el)),
  })),

  updateElementTransform: (elementId, patch) => set((s) => ({
    elements: s.elements.map((el) => (
      el.element_id === elementId
        ? { ...el, transform: normalizeTransform({ ...el.transform, ...patch }) }
        : el)),
  })),

  updateElementStyle: (elementId, patch) => set((s) => ({
    elements: s.elements.map((el) => {
      if (el.element_id !== elementId || el.type !== 'text') return el;
      const merged = { ...el.style };
      Object.entries(patch || {}).forEach(([key, value]) => {
        merged[key] = (value && typeof value === 'object' && !Array.isArray(value))
          ? { ...(merged[key] || {}), ...value }
          : value;
      });
      return { ...el, style: normalizeTextStyle(merged) };
    }),
  })),

  applyElementPreset: (elementId, presetName) => {
    const { style, transform } = applyPreset(presetName);
    set((s) => ({
      elements: s.elements.map((el) => (
        el.element_id === elementId && el.type === 'text'
          ? {
            ...el,
            style: normalizeTextStyle({ ...style, content: el.style?.content }),
            transform: normalizeTransform({ ...el.transform, ...transform }),
          }
          : el)),
    }));
  },

  removeElement: (elementId) => set((s) => ({
    elements: s.elements.filter((el) => el.element_id !== elementId),
    selectedClip: s.selectedClip?.id === elementId ? null : s.selectedClip,
  })),

  duplicateElement: (elementId) => {
    const source = get().elements.find((el) => el.element_id === elementId);
    if (!source) return null;
    const copy = normalizeElement({
      ...source,
      element_id: `el_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      start: source.start + source.duration,
      z_order: get().elements.length,
    });
    set((s) => ({
      elements: [...s.elements, copy],
      selectedClip: { trackKind: copy.type, id: copy.element_id },
    }));
    return copy.element_id;
  },

  moveElementLayer: (elementId, delta) => set((s) => ({
    elements: normalizeElements(s.elements.map((el) => (
      el.element_id === elementId ? { ...el, z_order: el.z_order + delta } : el))),
  })),

  toggleElementVisible: (elementId) => set((s) => ({
    elements: s.elements.map((el) => (
      el.element_id === elementId ? { ...el, visible: !el.visible } : el)),
  })),

  /** Elementos activos en un instante, ya ordenados por capa. */
  getActiveElements: (t) => normalizeElements(get().elements).filter(
    (el) => el.visible && t >= el.start && t < el.start + el.duration),

  // ── Plantillas de estilo de texto (globales, no por proyecto) ──
  // Se guardan en localStorage con su propia versión, así un estilo favorito
  // se reutiliza en cualquier proyecto con un clic.
  textTemplates: [],

  loadTextTemplates: () => {
    try {
      const raw = localStorage.getItem(TEXT_TEMPLATES_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const list = Array.isArray(parsed?.templates) ? parsed.templates : [];
      set({ textTemplates: list });
      return list;
    } catch (_e) {
      set({ textTemplates: [] });
      return [];
    }
  },

  saveTextTemplate: (name, elementId) => {
    const element = get().elements.find((el) => el.element_id === elementId);
    if (!element || element.type !== 'text') return null;
    const { content, ...style } = normalizeTextStyle(element.style);
    const template = {
      id: `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name: String(name || 'Mi estilo').slice(0, 40),
      schema_version: TEXT_TEMPLATE_SCHEMA_VERSION,
      element_model_version: ELEMENT_MODEL_VERSION,
      style,
      transform: {
        y_pct: element.transform.y_pct,
        rotation: element.transform.rotation,
        opacity: element.transform.opacity,
      },
    };
    const templates = [...get().textTemplates, template];
    set({ textTemplates: templates });
    try {
      localStorage.setItem(TEXT_TEMPLATES_KEY, JSON.stringify({
        schema_version: TEXT_TEMPLATE_SCHEMA_VERSION, templates,
      }));
    } catch (_e) { /* cuota / modo privado */ }
    return template.id;
  },

  applyTextTemplate: (templateId, elementId) => {
    const template = get().textTemplates.find((t) => t.id === templateId);
    if (!template) return false;
    set((s) => ({
      elements: s.elements.map((el) => (
        el.element_id === elementId && el.type === 'text'
          ? {
            ...el,
            style: normalizeTextStyle({ ...template.style, content: el.style?.content,
              preset: 'custom' }),
            transform: normalizeTransform({ ...el.transform, ...template.transform }),
          }
          : el)),
    }));
    return true;
  },

  removeTextTemplate: (templateId) => {
    const templates = get().textTemplates.filter((t) => t.id !== templateId);
    set({ textTemplates: templates });
    try {
      localStorage.setItem(TEXT_TEMPLATES_KEY, JSON.stringify({
        schema_version: TEXT_TEMPLATE_SCHEMA_VERSION, templates,
      }));
    } catch (_e) { /* cuota / modo privado */ }
  },

  // ── Persistencia del proyecto (timeline completo, versionado) ──
  serializeTimeline: () => {
    const s = get();
    return {
      schema_version: TIMELINE_SCHEMA_VERSION,
      element_model_version: ELEMENT_MODEL_VERSION,
      resolution: s.resolution,
      fps: s.fps,
      videoClips: s.videoClips,
      imageClips: s.imageClips,
      overlayV1Clips: s.overlayV1Clips,
      overlayV2Clips: s.overlayV2Clips,
      audioClips: s.audioClips,
      elements: s.elements,
      videoTransitions: s.videoTransitions,
      slots: s.slots,
      text_variables: s.textVariables,
      template_ref: s.templateRef,
      trackOrder: s.trackOrder,
      tracks: s.tracks,
      masterMuted: s.masterMuted,
      masterVolume: s.masterVolume,
    };
  },

  /** Carga un snapshot con defaults seguros: proyectos viejos no traen `elements`.
   *  `preserveView` (sync en vivo de LMX): no toca cabezal, selección ni
   *  reproducción; la selección que apuntaba a algo borrado se limpia. */
  hydrateTimeline: (snapshot, { preserveView = false } = {}) => {
    if (!snapshot || typeof snapshot !== 'object') return false;
    set({
      resolution: snapshot.resolution || '1080p',
      fps: snapshot.fps || 30,
      videoClips: snapshot.videoClips || [],
      imageClips: snapshot.imageClips || [],
      overlayV1Clips: snapshot.overlayV1Clips || [],
      overlayV2Clips: snapshot.overlayV2Clips || [],
      audioClips: snapshot.audioClips || [],
      elements: normalizeElements(snapshot.elements),
      videoTransitions: snapshot.videoTransitions || [],
      slots: snapshot.slots || [],
      textVariables: snapshot.text_variables || [],
      templateRef: snapshot.template_ref || null,
      trackOrder: normalizeTrackOrder(snapshot.trackOrder),
      tracks: {
        base: { ...TRACK_DEFAULTS, ...(snapshot.tracks?.base || {}) },
        v1: { ...TRACK_DEFAULTS, ...(snapshot.tracks?.v1 || {}) },
        v2: { ...TRACK_DEFAULTS, ...(snapshot.tracks?.v2 || {}) },
        audio: { ...TRACK_DEFAULTS, ...(snapshot.tracks?.audio || {}) },
      },
      masterMuted: !!snapshot.masterMuted,
      masterVolume: snapshot.masterVolume ?? MIX_DEFAULTS.master_volume,
      ...(preserveView ? {} : { selectedClip: null, playheadTime: 0, isPlaying: false, imageEdit: null }),
    });
    if (preserveView) {
      const s = get();
      if (!selectionExists(s, s.selectedClip)) set({ selectedClip: null });
    }
    // Lo hidratado ES lo que tiene el servidor: base del "sin rebote" del autosave.
    set({ savedSignature: docSignatureOf(get()) });
    return true;
  },

  // ── Render job ──────────────────────────────────────────────
  setRenderJob:   (job) => set({ renderJob: job }),
  clearRenderJob: () => set({ renderJob: null }),

  // ── E2 · sincronización con el servidor ─────────────────────
  // El servidor es la fuente de verdad; localStorage sigue siendo respaldo.
  syncStatus: 'idle',          // 'idle' | 'saving' | 'saved' | 'offline'
  serverVersion: null,
  syncConflict: null,          // { serverVersion } → lo muestra el editor

  setSyncStatus: (syncStatus) => set({ syncStatus }),
  setServerVersion: (serverVersion) => set({ serverVersion }),
  setSyncConflict: (syncConflict) => set({ syncConflict }),

  // ── E6 · Timeline en vivo (LMX) ───────────────────────────────────────
  /** Firma del doc tal como quedó en el servidor (hidratación o guardado). */
  savedSignature: null,
  /** Ids de clips/elementos que LMX acaba de tocar: pulso ~1,5 s en el editor. */
  agentFlash: [],

  markSaved: (signature, version) => set((s) => ({
    savedSignature: signature ?? docSignatureOf(s),
    ...(version != null ? { serverVersion: version } : {}),
    syncStatus: 'saved',
  })),

  flashAgentClips: (ids) => {
    const list = [...new Set(ids || [])];
    if (!list.length) return;
    set({ agentFlash: list });
    if (flashTimer) clearTimeout(flashTimer);
    flashTimer = setTimeout(() => set({ agentFlash: [] }), 1600);
  },

  /** Guardado inmediato (la misma lógica del autosave, sin debounce). Lo usa
   *  el editor (cada ~1 s) y LmxDock: flush ANTES de mandar un mensaje a LMX
   *  con el timeline abierto, así el agente parte siempre del doc fresco. */
  saveNow: async (projectId) => {
    // Envío AP-9 · un solo guardado a la vez. Si salían dos con la misma
    // versión base, el servidor aceptaba uno y rechazaba el otro como si
    // «alguien» hubiera cambiado el timeline: la ventana de conflicto salía
    // por un cambio propio de un segundo antes.
    if (saveInFlight) { saveAgain = true; return saveInFlight; }
    saveInFlight = get()._saveOnce(projectId);
    try {
      return await saveInFlight;
    } finally {
      saveInFlight = null;
      if (saveAgain) {
        saveAgain = false;
        get().saveNow(projectId);
      }
    }
  },

  _saveOnce: async (projectId) => {
    const s = get();
    if (!TIMELINE_SERVER_SYNC || !projectId || s.syncConflict) return { ok: false };
    const snapshot = get().serializeTimeline();
    const signature = docSignatureOf(snapshot);
    if (signature === s.savedSignature) return { ok: true, skipped: true };
    set({ syncStatus: 'saving' });
    try {
      const out = await putTimeline(projectId, snapshot, s.serverVersion);
      get().markSaved(signature, out.version);
      return { ok: true };
    } catch (err) {
      if (isConflict(err)) {
        // Si llegó al PUT es porque HAY cambios locales sin guardar
        // (signature !== savedSignature): NO se descartan en silencio.
        // Conflicto → el usuario decide en el modal (Recargar del servidor /
        // Mantener lo mío). Mientras el conflicto esté pendiente, el autosave
        // no reintenta (la guarda de arriba corta antes).
        set({ syncConflict: { serverVersion: serverVersionFrom(err) },
              syncStatus: 'conflict' });
        return { ok: false, conflict: true };
      }
      set({ syncStatus: 'offline' });
      return { ok: false };
    }
  },

  /**
   * Trae lo que LMX cambió en el servidor. SÓLO con el editor abierto; un
   * solo pedido por llamada (el debounce lo pone LmxDock). Sin cambios
   * locales: hidrata en silencio preservando la vista y destella lo
   * nuevo/cambiado. Con cambios locales: aviso de conflicto (regla E1/E2),
   * no se pisa nada.
   */
  syncTimelineFromAgent: async (projectId) => {
    const s = get();
    if (!s.isOpen) return { ok: true, changed: false };
    if (s.syncConflict) return { ok: false, conflict: true };
    let remote;
    try {
      remote = await fetchTimeline(projectId);
    } catch (_e) { return { ok: false }; }        // offline: la próxima vez
    if (!remote) return { ok: true, changed: false };
    if (remote.version != null && s.serverVersion != null
        && remote.version <= s.serverVersion) return { ok: true, changed: false };
    if (docSignatureOf(get()) !== s.savedSignature) {
      set({ syncConflict: { serverVersion: remote.version } });
      return { ok: false, conflict: true };
    }
    const before = snapshotByIds(get());
    get().hydrateTimeline(remote.data, { preserveView: true });
    set({ serverVersion: remote.version, syncStatus: 'saved' });
    const changedIds = diffSnapshotIds(before, snapshotByIds(get()));
    get().flashAgentClips(changedIds);
    return { ok: true, changed: true, changedIds };
  },

  /** Conflicto · "Recargar del servidor": descarta lo local e hidrata lo del
   *  servidor (hydrateTimeline ya deja savedSignature = lo hidratado). */
  resolveTimelineConflictReload: async (projectId) => {
    try {
      const remote = await fetchTimeline(projectId);
      if (!remote) return false;
      get().hydrateTimeline(remote.data);
      set({ serverVersion: remote.version, syncConflict: null, syncStatus: 'saved' });
      return true;
    } catch (_e) {
      return false;                               // offline: el modal queda
    }
  },

  /** Conflicto · "Mantener lo mío": re-PUT pisando la versión del servidor a
   *  conciencia (base_version = la que rechazó). Si vuelven a pisar, el modal
   *  queda con la versión nueva. */
  resolveTimelineConflictKeepMine: async (projectId) => {
    const s = get();
    const baseVersion = s.syncConflict?.serverVersion ?? null;
    const snapshot = get().serializeTimeline();
    const signature = docSignatureOf(snapshot);
    set({ syncStatus: 'saving' });
    try {
      const out = await putTimeline(projectId, snapshot, baseVersion);
      get().markSaved(signature, out.version);
      set({ syncConflict: null });
      return true;
    } catch (err) {
      if (isConflict(err)) {
        set({ syncConflict: { serverVersion: serverVersionFrom(err) },
              syncStatus: 'conflict' });
      } else {
        set({ syncStatus: 'offline' });
      }
      return false;
    }
  },

  /** Mueve un clip a otra pista si es compatible. `false` = incompatible. */
  moveClipToTrack: (fromKind, id, toKind, startTime = null) => {
    const fromKey = keyFor(fromKind);
    const toKey = keyFor(toKind);
    if (!fromKey || !toKey) return false;
    const s = get();
    const clip = (s[fromKey] || []).find((c) => c.id === id);
    if (!clip) return false;
    if (!trackAcceptsKind(toKind, clip.kind)) return false;

    const moved = startTime == null ? clip
      : { ...clip, start_time: Math.max(0, startTime) };
    if (fromKey === toKey) {
      set({ [toKey]: s[toKey].map((c) => (c.id === id ? moved : c)) });
      return true;
    }
    set({
      [fromKey]: s[fromKey].filter((c) => c.id !== id),
      [toKey]: [...(s[toKey] || []), moved],
    });
    return true;
  },
}));

// Testing hook — expose the store on `window.__timelineStore` so Playwright// tests can drive the editor without going through the file-upload UI.
// Safe to keep in prod: no secrets, no side effects until called.
if (typeof window !== 'undefined') {
  window.__timelineStore = useTimelineStore;
}

export default useTimelineStore;

/** px por segundo actual (base × zoom). Única fuente de escala del editor. */
export const usePxPerSec = () => useTimelineStore((s) => BASE_PX_PER_SEC * s.zoom);
